import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOrgTheme } from '../../context/OrgThemeContext';
import { NotificationBell } from '../notifications';
import { ADMIN_NAMES } from '../../config/teamConfig';

export type NavPage =
  | 'dashboard'
  | 'performance'
  | 'time-tracker'
  | 'team-dashboard'
  | 'estimate-review'
  | 'jira'
  | 'reports'
  | 'project-reports';

interface AppHeaderProps {
  currentPage: NavPage;
  onNavigate: (page: NavPage) => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ currentPage, onNavigate }) => {
  const { user, logout } = useAuth();
  const { brand } = useOrgTheme();

  // Check if current user is an admin
  const isAdmin = user?.username 
    ? ADMIN_NAMES.some(name => name.toLowerCase() === user.username.toLowerCase())
    : false;

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await logout();
    }
  };

  return (
    <header className="app-header">
      <div className="header-brand" onClick={() => onNavigate('dashboard')} style={{ cursor: 'pointer' }}>
        {brand.logoUrl ? (
          <img src={brand.logoUrl} alt={brand.headerTitle} className="header-brand-logo" />
        ) : (
          <h1>{brand.headerTitle}</h1>
        )}
      </div>
      
      <nav className="header-nav">
        <button 
          className={`nav-btn ${currentPage === 'dashboard' ? 'active' : ''}`}
          onClick={() => onNavigate('dashboard')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
          Dashboard
        </button>
        {isAdmin && (
          <div className="nav-dropdown">
            <button
              className={`nav-btn ${['reports', 'project-reports'].includes(currentPage) ? 'active' : ''}`}
              onClick={() => onNavigate('reports')}
              aria-haspopup="menu"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              Reports
              <svg className="nav-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div className="nav-dropdown-menu" role="menu">
              <button
                className={currentPage === 'reports' ? 'active' : ''}
                onClick={() => onNavigate('reports')}
                role="menuitem"
              >
                Employee Stats
              </button>
              <button
                className={currentPage === 'project-reports' ? 'active' : ''}
                onClick={() => onNavigate('project-reports')}
                role="menuitem"
              >
                Projects
              </button>
            </div>
          </div>
        )}
        <div className="nav-dropdown">
          <button
            className={`nav-btn ${['time-tracker', 'estimate-review'].includes(currentPage) ? 'active' : ''}`}
            onClick={() => onNavigate('time-tracker')}
            aria-haspopup={isAdmin ? 'menu' : undefined}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            Time Tracker
            {isAdmin && (
              <svg className="nav-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            )}
          </button>
          {isAdmin && (
            <div className="nav-dropdown-menu" role="menu">
              <button
                className={currentPage === 'time-tracker' ? 'active' : ''}
                onClick={() => onNavigate('time-tracker')}
                role="menuitem"
              >
                Time Tracker
              </button>
              <button
                className={currentPage === 'estimate-review' ? 'active' : ''}
                onClick={() => onNavigate('estimate-review')}
                role="menuitem"
              >
                Estimate Review
              </button>
            </div>
          )}
        </div>
        <button
          className={`nav-btn ${currentPage === 'performance' ? 'active' : ''}`}
          onClick={() => onNavigate('performance')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="20" x2="18" y2="10"></line>
            <line x1="12" y1="20" x2="12" y2="4"></line>
            <line x1="6" y1="20" x2="6" y2="14"></line>
          </svg>
          Performance
        </button>
        <button
          className={`nav-btn ${currentPage === 'jira' ? 'active' : ''}`}
          onClick={() => onNavigate('jira')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 13h4l3 8 4-16 3 8h4" />
          </svg>
          Jira
        </button>
        {isAdmin && (
          <button 
            className={`nav-btn ${currentPage === 'team-dashboard' ? 'active' : ''}`}
            onClick={() => onNavigate('team-dashboard')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            Team
          </button>
        )}
      </nav>

      <div className="header-actions">
        <NotificationBell />
        <div className="user-menu">
          {user && (
            <span className="user-greeting">
              <svg
                className="user-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              {user.username}
            </span>
          )}
          <button onClick={handleLogout} className="btn btn-logout" title="Sign out">
          <svg
            className="btn-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
            Sign Out
        </button>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
